import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import {
  DELIVERY_STATUS_ERROR,
  DELIVERY_STATUS_POLL_INTERVAL_MS,
  shouldPollDeliveryStatus,
} from "./deliveryStatusConstants";


export function useDeliveryStatus(pincode) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(document.visibilityState === "hidden");
  const requestRef = useRef(null);
  const pollCountRef = useRef(0);
  const mountedRef = useRef(false);

  const refresh = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/serviceability/status", { signal: controller.signal });
      if (mountedRef.current) setPayload(data);
    } catch (requestError) {
      if (!controller.signal.aborted && mountedRef.current) setError(DELIVERY_STATUS_ERROR);
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (!controller.signal.aborted && mountedRef.current) setLoading(false);
    }
  }, []);

  const restartAndRefresh = useCallback(() => {
    pollCountRef.current = 0;
    return refresh();
  }, [refresh]);

  useEffect(() => {
    mountedRef.current = true;
    if (document.visibilityState !== "hidden") restartAndRefresh();
    return () => {
      mountedRef.current = false;
      requestRef.current?.abort();
    };
  }, [pincode, restartAndRefresh]);

  useEffect(() => {
    const visibilityChanged = () => {
      const isHidden = document.visibilityState === "hidden";
      setHidden(isHidden);
      if (!isHidden) restartAndRefresh();
    };
    document.addEventListener("visibilitychange", visibilityChanged);
    return () => document.removeEventListener("visibilitychange", visibilityChanged);
  }, [restartAndRefresh]);

  useEffect(() => {
    const platforms = payload?.platforms || [];
    if (!shouldPollDeliveryStatus(platforms, pollCountRef.current, hidden, Boolean(error), loading)) return undefined;
    const timer = window.setTimeout(() => {
      pollCountRef.current += 1;
      refresh();
    }, DELIVERY_STATUS_POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [error, hidden, loading, payload, refresh]);

  return { payload, error, loading, refresh: restartAndRefresh };
}